import React, { useState } from "react";
import { ArrowRight, Check, Bell } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "../WaitlistForm";

const BOTTLE =
  "https://i.ibb.co/NdQKzXM6/Botella-con-fondo-oscuro-premium-estrecho.png";

export default function Product() {
  const { t } = useLang();
  const features = t("product.specs");
  const highlights = t("product.highlights");
  const p = t("product");
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <section
      id="producto"
      data-testid="product-section"
      className="relative bg-cream-100 py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Image */}
        <div className="relative flex justify-center">
          <img
            src={BOTTLE}
            alt={p.alt || "AOVE Cortijo Santa María 500 ml"}
            className="max-h-[560px] w-auto object-contain"
          />
        </div>

        {/* Text */}
        <div>
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
            {p.overline}
          </span>
          <h2 className="mt-6 font-serif text-5xl lg:text-6xl text-olive-900 font-medium leading-[0.98]">
            {p.title_a} <span className="italic">{p.title_b}</span>
            <br />
            {p.title_c}
          </h2>
          <p className="mt-8 text-olive-800/80 font-light leading-relaxed">{p.body}</p>

          {/* Reference note for pre-harvest — before any spec data */}
          {PRE_HARVEST_MODE && (
            <p
              data-testid="product-reference-note"
              className="mt-6 text-xs text-olive-800/60 border-l-2 border-gold-deep pl-3 italic"
            >
              {t("pre_harvest.reference_note")}
            </p>
          )}

          {/* Specs grid */}
          {features && features.length > 0 && (
            <dl className="mt-8 grid grid-cols-2 gap-3">
              {features.map((s) => (
                <div key={s.k} className="border border-cream-400 px-4 py-3">
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-olive-800/60">{s.k}</dt>
                  <dd className="mt-1 text-olive-900 font-serif text-xl">{s.v}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* Highlights */}
          {highlights && highlights.length > 0 && (
            <ul className="mt-8 space-y-2">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-olive-800/85">
                  <Check size={14} strokeWidth={1.5} className="mt-0.5 text-gold-deep shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          )}

          {/* Price / CTA */}
          <div className="mt-10">
            {PRE_HARVEST_MODE ? (
              <>
                <button
                  type="button"
                  data-testid="product-section-waitlist-btn"
                  onClick={() => setShowWaitlist(true)}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors"
                >
                  <Bell size={14} strokeWidth={1.4} />
                  {t("pre_harvest.cta_waitlist")}
                </button>
                <p className="mt-3 text-xs text-olive-800/60">
                  {t("pre_harvest.trust_line")}
                </p>
              </>
            ) : (
              <>
                {p.price && (
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="font-serif text-4xl text-olive-900">{p.price}</span>
                    {p.price_unit && (
                      <span className="text-sm text-olive-800/60">{p.price_unit}</span>
                    )}
                  </div>
                )}
                <a
                  href="/producto/botella-500ml"
                  data-testid="product-section-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors group"
                >
                  {p.cta}
                  <ArrowRight
                    size={14}
                    strokeWidth={1.4}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
                <p className="mt-3 text-xs text-olive-800/60">{p.shipping}</p>
              </>
            )}
          </div>
        </div>
      </div>
      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          preselectedProduct="botella-500ml"
        />
      )}
    </section>
  );
}
